import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import Loader from "../ui/Loader";
import { useAuth } from "../../hooks/AuthContext";

function AttendancePage() {
    const { user } = useAuth();
    const API_URL = process.env.REACT_APP_API_URL;

    const [attendanceData, setAttendanceData] = useState([]);
    const [loading, setLoading] = useState(false);

    const [isPunchInDisabled, setIsPunchInDisabled] = useState(true);
    const [isPunchOutDisabled, setIsPunchOutDisabled] = useState(true);

    const employeeId = user?.employeeProfile?._id;

    // Fetch today's attendance
    useEffect(() => {
        const fetchTodayAttendance = async () => {
            setLoading(true);
            try {
                const res = await axios.get(`${API_URL}attendance?employeeId=${employeeId}`);
                const records = res.data.data || [];

                const startOfDay = Math.floor(new Date().setHours(0, 0, 0, 0) / 1000);
                const endOfDay = Math.floor(new Date().setHours(23, 59, 59, 999) / 1000);

                const today = records.find(
                    (rec) => rec.attendanceDate >= startOfDay && rec.attendanceDate <= endOfDay
                );

                if (today) {
                    setIsPunchInDisabled(today.punchIn);
                    setIsPunchOutDisabled(!today.punchIn || today.punchOut);
                } else {
                    setIsPunchInDisabled(false);
                    setIsPunchOutDisabled(true);
                }

                setAttendanceData(records);
            } catch (err) {
                console.error(err);
                toast.error("Failed to fetch attendance");
            } finally {
                setLoading(false);
            }
        };

        fetchTodayAttendance();
    }, [employeeId, API_URL]);

    const handlePunchIn = async () => {
        const attendanceDate = Math.floor(new Date().setHours(0, 0, 0, 0) / 1000);
        const punchInTime = Math.floor(Date.now() / 1000);

        try {
            const res = await axios.post(`${API_URL}attendance`, {
                employeeId,
                attendanceDate,
                type: "punchIn",
                punchTime: punchInTime,
            });

            if (res.data.success) {
                toast.success("Punch-In successful!");
                setIsPunchInDisabled(true);
                setIsPunchOutDisabled(false);

                const newRecord = {
                    ...res.data.data,
                    name: user.employeeProfile.name,
                };

                setAttendanceData((prev) => {
                    const filtered = prev.filter((a) => a.attendanceDate !== attendanceDate);
                    return [...filtered, newRecord].sort((a, b) => b.attendanceDate - a.attendanceDate);
                });
            } else {
                toast.error(res.data.message);
            }
        } catch (err) {
            console.error(err);
            toast.error("Error punching in");
        }
    };

    const handlePunchOut = async () => {
        const attendanceDate = Math.floor(new Date().setHours(0, 0, 0, 0) / 1000);
        const punchOutTime = Math.floor(Date.now() / 1000);

        try {
            const res = await axios.post(`${API_URL}attendance`, {
                employeeId,
                attendanceDate,
                type: "punchOut",
                punchTime: punchOutTime,
            });

            if (res.data.success) {
                toast.success("Punch-Out successful!");
                setIsPunchOutDisabled(true);

                const newRecord = {
                    ...res.data.data,
                    name: user.employeeProfile.name,
                };

                setAttendanceData((prev) => {
                    const filtered = prev.filter((a) => a.attendanceDate !== attendanceDate);
                    return [...filtered, newRecord].sort((a, b) => b.attendanceDate - a.attendanceDate);
                });
            } else {
                toast.error(res.data.message);
            }
        } catch (err) {
            console.error(err);
            toast.error("Error punching out");
        }
    };

    const formatDate = (epoch) => new Date(epoch * 1000).toLocaleDateString();
    const formatTime = (epoch) => new Date(epoch * 1000).toLocaleTimeString();
    const getStatusBadge = (status) => {
        if (!status)
            return <span className="px-3 py-1 text-xs rounded-md bg-gray-100 text-gray-500">—</span>;

        const normalized = status.toLowerCase().replace(/\s|-/g, ""); // converts "on-time" or "on time" → "ontime"

        switch (normalized) {
            case "late":
                return (
                    <span className="px-3 py-1 text-xs font-medium rounded-md bg-red-50 text-red-500">
                        Late
                    </span>
                );
            case "ontime":
                return (
                    <span className="px-3 py-1 text-xs font-medium rounded-md bg-green-50 text-green-500">
                        On Time
                    </span>
                );
            default:
                return (
                    <span className="px-3 py-1 text-xs font-medium rounded-md bg-gray-50 text-gray-600">
                        {status}
                    </span>
                );
        }
    };


    return (
        <div className="p-4 bg-white rounded-lg flex flex-col gap-4">
            <h1 className="text-2xl font-bold mb-4">Attendance</h1>

            <div className="flex gap-4 mb-4">
                <button
                    onClick={handlePunchIn}
                    disabled={isPunchInDisabled}
                    className={`px-4 py-2 rounded bg-green-500 text-white ${isPunchInDisabled ? "opacity-50 cursor-not-allowed" : "hover:bg-green-600"}`}
                >
                    Punch In
                </button>
                <button
                    onClick={handlePunchOut}
                    disabled={isPunchOutDisabled}
                    className={`px-4 py-2 rounded bg-red-500 text-white ${isPunchOutDisabled ? "opacity-50 cursor-not-allowed" : "hover:bg-red-600"}`}
                >
                    Punch Out
                </button>
            </div>

            <div className="overflow-x-auto rounded-lg shadow border">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Employee</th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Date</th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Attendance</th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Punch In</th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Punch In Time</th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Punch Out</th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Punch Out Time</th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200 min-h-[200px]">
                        {loading ? (
                            <tr>
                                <td colSpan={8} className="py-20 flex justify-center items-center">
                                    <Loader />
                                </td>
                            </tr>
                        ) : attendanceData.length > 0 ? (
                            attendanceData.map((att, idx) => (
                                <tr key={idx} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 text-center">{att.name || "N/A"}</td>
                                    <td className="px-6 py-4 text-center">{formatDate(att.attendanceDate)}</td>
                                    <td className="px-6 py-4 text-center">
                                        {att.present ? "Present" : att.leave ? "Leave" : "—"}
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        {att.punchIn ? (
                                            <span className="px-3 py-1 text-xs font-medium rounded-md bg-green-50 text-green-500">
                                                P
                                            </span>
                                        ) : (
                                            <span className="px-3 py-1 text-xs rounded-md bg-gray-100 text-gray-500">—</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 text-center">{att.punchInTime ? formatTime(att.punchInTime) : "—"}</td>
                                    <td className="px-6 py-4 text-center">
                                        {att.punchOut ? (
                                            <span className="px-3 py-1 text-xs font-medium rounded-md bg-red-50 text-red-500">
                                                L
                                            </span>
                                        ) : (
                                            <span className="px-3 py-1 text-xs rounded-md bg-gray-100 text-gray-500">—</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 text-center">{att.punchOutTime ? formatTime(att.punchOutTime) : "—"}</td>
                                    <td className="px-6 py-4 text-center">{getStatusBadge(att.status)}</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={8} className="text-center text-gray-500 py-6 text-sm">
                                    No attendance records found
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default AttendancePage;
